import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Folder, FolderOpen, Inbox } from 'lucide-react';
import type { WorkspaceFolder } from '../../utils/workspaceFolders';
import { buildFolderTree, getFolderPath, type FolderNode } from '../../utils/folderTree';
import { formatCost } from '../../utils/workspaceLibrary';
import { PeopleBadge, ProjectTile, WorkspaceThumb } from './ProjectTile';

export interface TreeDropHandlers {
    /** Row currently hovered by a drag: a folder id, null for the root row, undefined for none. */
    activeTargetId: string | null | undefined;
    onDragOver: (e: React.DragEvent, targetId: string | null) => void;
    onDragLeave: (e: React.DragEvent, targetId: string | null) => void;
    onDrop: (e: React.DragEvent, targetId: string | null) => void;
}

/** A non-folder item shown inside the outline (a workspace). */
export interface TreeLeaf {
    id: string;
    label: string;
    thumbnail?: string;
    /** The item that is open right now (rendered highlighted, aria-current). */
    current?: boolean;
    /** Small secondary text, e.g. "On this device". */
    note?: string;
    /** What it cost, in US dollars: shown at the row's end when more than nothing */
    cost?: number;
}

export interface FolderTreeProps {
    folders: WorkspaceFolder[];
    /** Recursive workspace counts (see getWorkspaceCountsByFolder). */
    counts?: Map<string, number>;
    /** What each folder's workspaces cost together, sub-folders' included (see getWorkspaceCostsByFolder) */
    costs?: Map<string, number>;
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    expandedIds: Set<string>;
    onToggleExpand: (id: string) => void;
    /** Folders that are always open and cannot be collapsed (e.g. the project at the top of an outline). */
    alwaysExpandedIds?: Set<string>;
    /** 'nav' shows kebab menus and accepts drops; 'picker' shows disabled reasons. */
    mode?: 'nav' | 'picker';
    /** 'outline' is the Finder-like look: filled folder icons, leaves nested under folders. */
    variant?: 'default' | 'outline';
    /** When set, a root row (id null) is rendered first with this label. */
    rootLabel?: string;
    rootCount?: number;
    rootCost?: number;
    disabledReason?: (id: string | null) => string | undefined;
    renderMenu?: (folder: WorkspaceFolder) => React.ReactNode;
    /** Actions for a leaf (a workspace), revealed on hover like a folder's. */
    renderLeafMenu?: (leaf: TreeLeaf) => React.ReactNode;
    /** A chip after a folder's name: what this person may do in a project shared with them */
    renderBadge?: (folder: WorkspaceFolder) => React.ReactNode;
    drop?: TreeDropHandlers;
    /** Case-insensitive name filter: matches and their ancestors stay visible and expanded. */
    filter?: string;
    ariaLabel: string;
    /** Leaves grouped by their parent folder id (null = under the root row, or top level). */
    leaves?: Map<string | null, TreeLeaf[]>;
    onSelectLeaf?: (leaf: TreeLeaf) => void;
    onLeafDragStart?: (e: React.DragEvent, leaf: TreeLeaf) => void;
}

interface Row {
    key: string;
    kind: 'folder' | 'leaf';
    id: string | null;
    folder?: WorkspaceFolder;
    leaf?: TreeLeaf;
    depth: number;
    hasChildren: boolean;
    expanded: boolean;
    parentKey: string | null;
    /** Folder id this row's drop target maps to (folders and the root row only). */
    dropId?: string | null;
}

// A row's actions button. With a pointer it lies over the right end of the row and shows when
// the row is pointed at or has the focus (or its menu is open): a row with a count shows the
// button in the count's place, so counts stay in one column with the rows above; a row with no
// count keeps the room free. On a touch screen there is no hover, the button always shows, and
// it stays in the row after the count.
const MENU_PLACE = 'ns-lib-row-reveal flex flex-none -mr-1 [&>button:hover]:bg-white/70 has-[[aria-expanded=true]]:opacity-100 [@media(hover:hover)]:absolute [@media(hover:hover)]:right-1 [@media(hover:hover)]:top-1/2 [@media(hover:hover)]:-translate-y-1/2 [@media(hover:hover)]:mr-0';
const MENU_ROOM = '[@media(hover:hover)]:pr-9';
const COUNT_UNDER_MENU = 'min-w-5 text-right transition-opacity [@media(hover:hover)]:group-hover/row:opacity-0 [@media(hover:hover)]:group-focus-within/row:opacity-0 [@media(hover:hover)]:group-has-[[aria-haspopup=menu][aria-expanded=true]]/row:opacity-0';

const folderKey = (id: string | null) => (id === null ? '__root__' : `f:${id}`);
const leafKey = (id: string) => `l:${id}`;

/**
 * Flat WAI-ARIA tree (role=tree with aria-level rows) used by the sidebars and by
 * the Move / Location pickers. One roving tab stop; arrow keys move and
 * expand/collapse; Enter or Space selects. Optional leaves (workspaces) nest
 * under their folder like files in a Finder outline.
 */
export const FolderTree: React.FC<FolderTreeProps> = ({
    folders,
    counts,
    costs,
    selectedId,
    onSelect,
    expandedIds,
    onToggleExpand,
    alwaysExpandedIds,
    mode = 'nav',
    variant = 'default',
    rootLabel,
    rootCount,
    rootCost,
    disabledReason,
    renderMenu,
    renderLeafMenu,
    renderBadge,
    drop,
    filter,
    ariaLabel,
    leaves,
    onSelectLeaf,
    onLeafDragStart,
}) => {
    const tree = useMemo(() => buildFolderTree(folders), [folders]);
    const query = (filter || '').trim().toLowerCase();
    const outline = variant === 'outline';

    // Filtering: keep matches and every ancestor of a match; force-expand ancestors.
    const { visibleIds, forcedExpanded } = useMemo(() => {
        if (!query) return { visibleIds: null as Set<string> | null, forcedExpanded: new Set<string>() };
        const visible = new Set<string>();
        const forced = new Set<string>();
        for (const f of folders) {
            if (f.name.toLowerCase().includes(query)) {
                visible.add(f.id);
                for (const a of getFolderPath(folders, f.id)) {
                    visible.add(a.id);
                    if (a.id !== f.id) forced.add(a.id);
                }
            }
        }
        return { visibleIds: visible, forcedExpanded: forced };
    }, [folders, query]);

    const rows = useMemo(() => {
        const out: Row[] = [];
        const leavesOf = (parentId: string | null): TreeLeaf[] => {
            const list = leaves?.get(parentId) || [];
            return query ? list.filter(l => l.label.toLowerCase().includes(query)) : list;
        };
        const pushLeaves = (parentId: string | null, depth: number, parentKey: string | null) => {
            for (const leaf of leavesOf(parentId)) {
                out.push({ key: leafKey(leaf.id), kind: 'leaf', id: leaf.id, leaf, depth, hasChildren: false, expanded: false, parentKey });
            }
        };
        const walk = (nodes: FolderNode[], depth: number, parentKey: string | null) => {
            for (const node of nodes) {
                if (visibleIds && !visibleIds.has(node.folder.id)) continue;
                const kids = visibleIds ? node.children.filter(c => visibleIds.has(c.folder.id)) : node.children;
                const id = node.folder.id;
                const hasChildren = kids.length > 0 || leavesOf(id).length > 0;
                const pinned = !!alwaysExpandedIds?.has(id);
                const expanded = hasChildren && (pinned || forcedExpanded.has(id) || expandedIds.has(id));
                const key = folderKey(id);
                out.push({ key, kind: 'folder', id, folder: node.folder, depth, hasChildren, expanded, parentKey, dropId: id });
                if (expanded) {
                    walk(kids, depth + 1, key);
                    pushLeaves(id, depth + 1, key);
                }
            }
        };
        if (rootLabel !== undefined) {
            // The root row is a sibling of the projects (same level) and, when it has leaves, their parent.
            const rootLeaves = leavesOf(null);
            out.push({ key: folderKey(null), kind: 'folder', id: null, depth: 0, hasChildren: rootLeaves.length > 0, expanded: rootLeaves.length > 0, parentKey: null, dropId: null });
            walk(tree, 0, null);
            pushLeaves(null, 1, folderKey(null));
        } else {
            walk(tree, 0, null);
            pushLeaves(null, 0, null);
        }
        return out;
    }, [tree, expandedIds, alwaysExpandedIds, forcedExpanded, visibleIds, rootLabel, leaves, query]);

    // Roving tabindex
    const [focusKey, setFocusKey] = useState<string | undefined>(undefined);
    const containerRef = useRef<HTMLDivElement>(null);

    const focusRow = useCallback((key: string) => {
        setFocusKey(key);
        const el = containerRef.current?.querySelector<HTMLElement>(`[data-row-key="${key}"]`);
        el?.focus();
    }, []);

    useEffect(() => {
        if (focusKey !== undefined && !rows.some(r => r.key === focusKey)) setFocusKey(undefined);
    }, [rows, focusKey]);

    const selectedKey = rows.find(r => r.kind === 'folder' && r.id === selectedId)?.key;
    const currentKey = rows.find(r => r.kind === 'leaf' && r.leaf?.current)?.key;
    const tabStopKey: string | undefined = (() => {
        if (focusKey !== undefined && rows.some(r => r.key === focusKey)) return focusKey;
        return currentKey ?? selectedKey ?? (rows.length ? rows[0].key : undefined);
    })();

    const canToggle = (row: Row) => row.kind === 'folder' && row.hasChildren && !!row.id && !alwaysExpandedIds?.has(row.id);

    // The column for the disclosure arrow exists only where an arrow does: from the shallowest level
    // that has one, downwards (a level under one with arrows keeps the column, so its rows stay
    // further in than their parent). A list with no arrows at all starts at the row's edge, in line
    // with the plain rows above it in a sidebar.
    const arrowFromDepth = useMemo(
        () => rows.reduce((min, r) => (r.kind === 'folder' && r.id !== null && r.hasChildren ? Math.min(min, r.depth) : min), Infinity),
        [rows],
    );

    const activate = (row: Row) => {
        if (row.kind === 'leaf') { if (row.leaf) onSelectLeaf?.(row.leaf); return; }
        if (!disabledReason?.(row.id)) onSelect(row.id);
    };

    const onRowKeyDown = (e: React.KeyboardEvent, row: Row, index: number) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (index + 1 < rows.length) focusRow(rows[index + 1].key);
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (index > 0) focusRow(rows[index - 1].key);
                break;
            case 'ArrowRight':
                e.preventDefault();
                if (canToggle(row) && !row.expanded && row.id) onToggleExpand(row.id);
                else if (row.hasChildren && index + 1 < rows.length) focusRow(rows[index + 1].key);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                if (canToggle(row) && row.expanded && row.id) onToggleExpand(row.id);
                else if (row.parentKey) focusRow(row.parentKey);
                break;
            case 'Home':
                e.preventDefault();
                focusRow(rows[0].key);
                break;
            case 'End':
                e.preventDefault();
                focusRow(rows[rows.length - 1].key);
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                activate(row);
                break;
            case 'F10':
            case 'ContextMenu':
                // Shift+F10 / the context-menu key open the row's actions menu
                if (e.key === 'F10' && !e.shiftKey) break;
                e.preventDefault();
                (e.currentTarget as HTMLElement).querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]')?.click();
                break;
            default:
                break;
        }
    };

    if (rows.length === 0) return null;

    const indent = outline ? 18 : 16;

    return (
        <div ref={containerRef} role="tree" aria-label={ariaLabel} className={`flex flex-col ${outline ? '' : 'gap-0.5'}`}>
            {rows.map((row, index) => {
                const isLeaf = row.kind === 'leaf';
                const reason = isLeaf ? undefined : disabledReason?.(row.id);
                const selected = !isLeaf && row.id === selectedId;
                const count = isLeaf ? undefined : row.id === null ? rootCount : counts?.get(row.id);
                const cost = isLeaf ? row.leaf?.cost : row.id === null ? rootCost : costs?.get(row.id);
                const isDropTarget = !isLeaf && !!drop;
                const isDropActive = isDropTarget && drop!.activeTargetId !== undefined && drop!.activeTargetId === row.dropId;
                const isProject = row.folder?.kind === 'project';
                // Who a project is shared with is the owner's to see; on a project shared with this person the chip says what they may do
                const people = isProject && !row.folder?.isShared ? (row.folder?.sharedUserIds?.length ?? 0) : 0;
                const label = isLeaf ? row.leaf?.label : row.id === null ? rootLabel : row.folder?.name;
                const showChevron = row.hasChildren && (isLeaf ? false : row.id === null ? false : true);
                const showCount = typeof count === 'number' && !(reason && mode === 'picker');
                const menu = mode !== 'nav' ? null
                    : isLeaf ? (row.leaf && renderLeafMenu ? renderLeafMenu(row.leaf) : null)
                    : row.folder && renderMenu ? renderMenu(row.folder) : null;
                return (
                    <div
                        key={row.key}
                        role="treeitem"
                        aria-level={row.depth + 1}
                        aria-selected={isLeaf ? undefined : selected}
                        aria-current={isLeaf && row.leaf?.current ? 'page' : undefined}
                        aria-expanded={row.hasChildren && !isLeaf ? row.expanded : undefined}
                        aria-disabled={reason ? true : undefined}
                        aria-label={label}
                        data-row-key={row.key}
                        data-row-id={isLeaf ? undefined : row.id === null ? '__root__' : row.id}
                        tabIndex={row.key === tabStopKey ? 0 : -1}
                        className={`ns-lib-row relative group/row ${outline ? 'ns-lib-row-outline' : ''} ${isDropActive ? 'ns-lib-drop' : ''} ${(isLeaf && row.leaf?.current) || (outline && row.folder?.kind === 'project') ? 'font-semibold' : ''} ${menu && !showCount ? MENU_ROOM : ''}`}
                        style={{ paddingLeft: 8 + row.depth * indent }}
                        title={reason || (isLeaf ? row.leaf?.label : undefined)}
                        draggable={isLeaf && !!onLeafDragStart}
                        onDragStart={isLeaf && onLeafDragStart && row.leaf ? (e) => onLeafDragStart(e, row.leaf as TreeLeaf) : undefined}
                        onClick={() => activate(row)}
                        onFocus={() => setFocusKey(row.key)}
                        onKeyDown={(e) => onRowKeyDown(e, row, index)}
                        onDragOver={isDropTarget ? (e) => drop!.onDragOver(e, row.dropId ?? null) : undefined}
                        onDragLeave={isDropTarget ? (e) => drop!.onDragLeave(e, row.dropId ?? null) : undefined}
                        onDrop={isDropTarget ? (e) => drop!.onDrop(e, row.dropId ?? null) : undefined}
                    >
                        {showChevron ? (
                            <button
                                type="button"
                                tabIndex={-1}
                                aria-hidden="true"
                                // The row under it is already tinted on hover, so the button lights up in white
                                className={`ns-icon-btn ns-icon-btn-xs hover:bg-white/70 ${canToggle(row) ? '' : 'opacity-40 cursor-default'}`}
                                onClick={(e) => { e.stopPropagation(); if (canToggle(row) && row.id) onToggleExpand(row.id); }}
                            >
                                <ChevronRight className={`transition-transform ${row.expanded ? 'rotate-90' : ''}`} />
                            </button>
                        ) : row.depth >= arrowFromDepth ? (
                            <span className="w-6 h-6 flex-none" aria-hidden="true" />
                        ) : null}

                        {/* The icon's box is the one the plain sidebar rows use (20), so names start at the same place.
                            The marks: a project is its tile, a folder the folder icon, a workspace its picture. */}
                        <span className={`grid place-items-center flex-none ${outline ? '' : 'w-5 h-5'}`} aria-hidden="true">
                            {isLeaf ? (
                                <WorkspaceThumb thumbnail={row.leaf?.thumbnail} />
                            ) : row.id === null ? (
                                <Inbox className="w-4 h-4 text-ink-soft flex-none" />
                            ) : isProject && row.folder ? (
                                <ProjectTile name={row.folder.name} color={row.folder.color} size="sm" />
                            ) : row.expanded && outline ? (
                                <FolderOpen className="w-4 h-4 flex-none text-ink-soft" />
                            ) : (
                                <Folder className="w-4 h-4 text-ink-soft flex-none" />
                            )}
                        </span>

                        <span className="truncate flex-1 min-w-0">{label}</span>
                        {people > 0 && <PeopleBadge count={people} />}
                        {!isLeaf && row.folder && renderBadge?.(row.folder)}

                        {isLeaf && row.leaf?.note && (
                            <span className="ns-t-meta text-ink-soft whitespace-nowrap">{row.leaf.note}</span>
                        )}

                        {/* What it cost (a folder: its workspaces together) */}
                        {typeof cost === 'number' && cost > 0 && !(reason && mode === 'picker') && (
                            <span className="ns-t-meta text-ink-soft tabular-nums whitespace-nowrap" title="What its generations cost" data-row-cost>{formatCost(cost)}</span>
                        )}

                        {reason && mode === 'picker' ? (
                            <span className="ns-t-small whitespace-nowrap">{reason}</span>
                        ) : showCount ? (
                            <span className={`ns-lib-count ${menu ? COUNT_UNDER_MENU : ''}`}>{count}</span>
                        ) : null}

                        {menu && (
                            <span className={MENU_PLACE} onClick={(e) => e.stopPropagation()}>
                                {menu}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
